import { DOCUMENT } from '@angular/common';
import { computed, effect, inject } from '@angular/core';
import {
  ColorTheme,
  LocalStorageService,
  ThemeService,
} from '@marineform/fe-util';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withComputed,
  withState,
} from '@ngrx/signals';
import {
  AMBIENCE_AVATAR_THEMES,
  AMBIENCE_GRADIENT_CLASSES,
  AMBIENCES,
  AppearanceAmbience,
} from './appearance-ambience.config';

export type {
  AppearanceAmbience,
  AppearanceAmbienceOption,
} from './appearance-ambience.config';
export { APPEARANCE_AMBIENCE_OPTIONS } from './appearance-ambience.config';

export type AppearanceThemeMode = 'system' | 'light' | 'dark';
export type AppearanceFontScale = 90 | 95 | 100;
export type AppearanceFocusMode = 'zen' | 'advanced';

export type AppearanceState = {
  themeMode: AppearanceThemeMode;
  fontScale: AppearanceFontScale;
  focusMode: AppearanceFocusMode;
  ambience: AppearanceAmbience;
};

const STORAGE_KEY = 'manage.appearance';
const THEME_MODES: readonly AppearanceThemeMode[] = ['system', 'light', 'dark'];
const FONT_SCALES: readonly AppearanceFontScale[] = [90, 95, 100];
const FOCUS_MODES: readonly AppearanceFocusMode[] = ['zen', 'advanced'];
const DEFAULT_STATE: AppearanceState = {
  themeMode: 'system',
  fontScale: 100,
  focusMode: 'advanced',
  ambience: 'fresh',
};

function normalizeAmbience(stored: unknown): AppearanceAmbience {
  if (stored === 'overdrive') {
    return 'nitro';
  }
  if (stored != null && AMBIENCES.includes(stored as AppearanceAmbience)) {
    return stored as AppearanceAmbience;
  }
  return DEFAULT_STATE.ambience;
}

function readStoredState(storage: LocalStorageService): AppearanceState {
  try {
    const stored = storage.get<Partial<AppearanceState>>(STORAGE_KEY);
    if (stored == null) {
      return DEFAULT_STATE;
    }
    return {
      themeMode:
        stored.themeMode != null && THEME_MODES.includes(stored.themeMode)
          ? stored.themeMode
          : DEFAULT_STATE.themeMode,
      fontScale:
        stored.fontScale != null && FONT_SCALES.includes(stored.fontScale)
          ? stored.fontScale
          : DEFAULT_STATE.fontScale,
      focusMode:
        stored.focusMode != null && FOCUS_MODES.includes(stored.focusMode)
          ? stored.focusMode
          : DEFAULT_STATE.focusMode,
      ambience: normalizeAmbience(stored.ambience),
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function themeModeToForceScheme(
  themeMode: AppearanceThemeMode,
): ColorTheme | null {
  switch (themeMode) {
    case 'light':
      return ColorTheme.LIGHT;
    case 'dark':
      return ColorTheme.DARK;
    case 'system':
      return null;
  }
}

export const AppearanceStore = signalStore(
  { providedIn: 'root' },
  withState(DEFAULT_STATE),
  withComputed((store) => ({
    isZenMode: computed(() => store.focusMode() === 'zen'),
    ambienceGradientClasses: computed(
      () => AMBIENCE_GRADIENT_CLASSES[store.ambience()],
    ),
    ambienceAvatarTheme: computed(
      () => AMBIENCE_AVATAR_THEMES[store.ambience()],
    ),
  })),
  withMethods((store) => ({
    patch(partial: Partial<AppearanceState>): void {
      patchState(store, partial);
    },
  })),
  withHooks(
    (
      store,
      storage = inject(LocalStorageService),
      theme = inject(ThemeService),
      document = inject(DOCUMENT),
    ) => ({
      onInit() {
        patchState(store, readStoredState(storage));
        effect(() => {
          const themeMode = store.themeMode();
          const fontScale = store.fontScale();
          const focusMode = store.focusMode();
          const ambience = store.ambience();
          storage.set(STORAGE_KEY, {
            themeMode,
            fontScale,
            focusMode,
            ambience,
          } satisfies AppearanceState);
          theme.forceScheme.set(themeModeToForceScheme(themeMode));
          document.documentElement.dataset['manageFontScale'] =
            fontScale.toString();
        });
      },
    }),
  ),
);
